import { useState, useEffect } from "react";
import { Button } from "@app/components/shared/Button";
import { Card } from "@app/components/shared/Card";
import type { ActivePosition } from "@shared/types";

interface PositionControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  position: ActivePosition | null;
  onControl: (
    action: "partial_exit" | "full_exit" | "adjust_stop" | "adjust_target",
    params?: { quantity?: number; stop_loss?: number; take_profit?: number[] },
  ) => void;
}

export function PositionControlModal({
  isOpen,
  onClose,
  position,
  onControl,
}: PositionControlModalProps): JSX.Element | null {
  const [stopLoss, setStopLoss] = useState<string>("");
  const [takeProfit, setTakeProfit] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && position) {
      setStopLoss("");
      setTakeProfit("");
      setQuantity(position.quantity.toString());
      setError(null);
    }
  }, [isOpen, position]);

  if (!isOpen || !position) {
    return null;
  }

  function handleAdjustStop(): void {
    setError(null);
    const stopLossValue = parseFloat(stopLoss);
    if (isNaN(stopLossValue) || stopLossValue <= 0) {
      setError("Invalid stop loss value");
      return;
    }
    setIsSubmitting(true);
    try {
      onControl("adjust_stop", { stop_loss: stopLossValue });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to adjust stop loss");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleAdjustTarget(): void {
    setError(null);
    const takeProfitValue = parseFloat(takeProfit);
    if (isNaN(takeProfitValue) || takeProfitValue <= 0) {
      setError("Invalid take profit value");
      return;
    }
    setIsSubmitting(true);
    try {
      onControl("adjust_target", { take_profit: [takeProfitValue] });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to adjust take profit");
    } finally {
      setIsSubmitting(false);
    }
  }

  function handlePartialExit(): void {
    setError(null);
    if (!position) {
      setError("Position is not available.");
      return;
    }
    const quantityValue = parseFloat(quantity);
    if (isNaN(quantityValue) || quantityValue <= 0 || quantityValue > position.quantity) {
      setError(`Invalid quantity. Must be between 0 and ${position.quantity}`);
      return;
    }
    setIsSubmitting(true);
    try {
      onControl("partial_exit", { quantity: quantityValue });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to execute partial exit");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <Card
        className="modal-card"
        title={`Position Control: ${position.symbol}`}
        subtitle={`Side: ${position.side} | Entry: ${position.entry_price} | Quantity: ${position.quantity}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "16px", minWidth: "400px" }}>
          {error && (
            <div style={{ padding: "8px", marginBottom: "16px", background: "var(--color-error-light)", color: "var(--color-error)", borderRadius: "4px" }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Adjust Stop Loss
            </label>
            <input
              type="number"
              value={stopLoss}
              onChange={(e) => setStopLoss(e.target.value)}
              placeholder="Enter stop loss price"
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid var(--color-border)",
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={handleAdjustStop}
              disabled={isSubmitting}
              style={{ marginTop: "8px" }}
            >
              Update Stop Loss
            </Button>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Adjust Take Profit
            </label>
            <input
              type="number"
              value={takeProfit}
              onChange={(e) => setTakeProfit(e.target.value)}
              placeholder="Enter take profit price"
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid var(--color-border)",
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={handleAdjustTarget}
              disabled={isSubmitting}
              style={{ marginTop: "8px" }}
            >
              Update Take Profit
            </Button>
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold" }}>
              Partial Exit Quantity
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={`Enter quantity (max: ${position.quantity})`}
              style={{
                width: "100%",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid var(--color-border)",
              }}
            />
            <Button
              type="button"
              variant="ghost"
              size="small"
              onClick={handlePartialExit}
              disabled={isSubmitting}
              style={{ marginTop: "8px" }}
            >
              Execute Partial Exit
            </Button>
          </div>

          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "24px" }}>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

